import React, { Component, ReactNode } from 'react';
import { LuTriangleAlert } from 'react-icons/lu';
import { Button } from '@/components/ui/button';

interface MoneyErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface MoneyErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class MoneyErrorBoundary extends Component<
  MoneyErrorBoundaryProps,
  MoneyErrorBoundaryState
> {
  constructor(props: MoneyErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): MoneyErrorBoundaryState {
    // Check if this is a money-related error
    const isMoneyError =
      error?.message?.includes('You must provide an integer') ||
      error?.stack?.includes('dinero') ||
      error?.stack?.includes('MoneyFromCents') ||
      error?.stack?.includes('MoneyFromDollars');

    if (isMoneyError) {
      return { hasError: true, error };
    }

    // Re-throw non-money errors to be handled by parent boundaries
    throw error;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Call optional error callback which can handle logging
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Return custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default money error fallback
      return (
        <div className="flex flex-col items-center justify-center p-4 border border-orange-200 bg-orange-50 rounded-lg">
          <LuTriangleAlert className="w-8 h-8 text-orange-500 mb-2" />
          <p className="text-sm text-orange-700 text-center mb-3">
            Unable to display financial information
          </p>
          <p className="text-xs text-orange-600 text-center mb-3">
            There was an issue with the money calculations. This has been logged for investigation.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={this.handleRetry}
            className="text-orange-700 border-orange-300 hover:bg-orange-100"
          >
            Try Again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook version for easier usage
export const useMoneyErrorBoundary = () => {
  const [key, setKey] = React.useState(0);

  const resetBoundary = React.useCallback(() => {
    setKey((prev) => prev + 1);
  }, []);

  return { key, resetBoundary };
};

// Higher-order component to wrap components that display money
export const withMoneyErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode,
) => {
  const WrappedComponent = (props: P) => (
    <MoneyErrorBoundary fallback={fallback}>
      <Component {...props} />
    </MoneyErrorBoundary>
  );

  WrappedComponent.displayName = `withMoneyErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};
